import ApiService from '../api'

class WorkingGroupServices {
  // Get all Working Groups (active only)
  async getWorkingGroups() {
    try {
      return await ApiService.get('/working-groups')
    } catch (error) {
      console.error('Get working groups error:', error)
      throw error
    }
  }

  // Public, unauthenticated list of active working groups (id + name only).
  // Used by public pages like Our Fellows, which have no logged-in session.
  async getPublicWorkingGroups() {
    try {
      return await ApiService.get('/public/working-groups')
    } catch (error) {
      console.error('Get public working groups error:', error)
      throw error
    }
  }

  // Get all Working Groups including deleted ones
  async getAllWorkingGroups() {
    try {
      return await ApiService.get('/working-groups/all-including-deleted')
    } catch (error) {
      console.error('Get all working groups error:', error)
      throw error
    }
  }

  // Get specific Working Group
  async getWorkingGroup(id) {
    try {
      return await ApiService.get(`/working-groups/${id}`)
    } catch (error) {
      console.error('Get working group error:', error)
      throw error
    }
  }

  // Create a new Working Group
  async createWorkingGroup(data) {
    try {
      return await ApiService.post('/working-groups', {}, data)
    } catch (error) {
      console.error('Create working group error:', error)
      throw error
    }
  }

  // Update a specific Working Group
  async updateWorkingGroup(id, data) {
    try {
      return await ApiService.put(`/working-groups/${id}`, {}, data)
    } catch (error) {
      console.error('Update working group error:', error)
      throw error
    }
  }

  // Delete a specific Working Group (soft delete)
  async deleteWorkingGroup(id) {
    try {
      return await ApiService.delete(`/working-groups/${id}`)
    } catch (error) {
      console.error('Delete working group error:', error)
      throw error
    }
  }

  // Restore a soft deleted Working Group
  async restoreWorkingGroup(id) {
    try {
      return await ApiService.post(`/working-groups/${id}/restore`)
    } catch (error) {
      console.error('Restore working group error:', error)
      throw error
    }
  }

  // Activate Working Group
  async activateWorkingGroup(id) {
    try {
      return await ApiService.post(`/working-groups/${id}/activate`)
    } catch (error) {
      console.error('Activate working group error:', error)
      throw error
    }
  }

  // Deactivate Working Group
  async deactivateWorkingGroup(id) {
    try {
      return await ApiService.post(`/working-groups/${id}/deactivate`)
    } catch (error) {
      console.error('Deactivate working group error:', error)
      throw error
    }
  }

  // Get members linked to the Working Group
  async getWorkingGroupMembers(id) {
    try {
      return await ApiService.get(`/working-groups/${id}/members`)
    } catch (error) {
      console.error('Get working group members error:', error)
      throw error
    }
  }

  // Add members to Working Group (invite users)
  async addWorkingGroupMember(id, data) {
    try {
      return await ApiService.post(`/working-groups/${id}/invite`, {}, data)
    } catch (error) {
      console.error('Add working group member error:', error)
      throw error
    }
  }

  // Remove member from Working Group
  async removeMember(workingGroupId, userId) {
    try {
      return await ApiService.delete(`/working-groups/${workingGroupId}/members/${userId}`)
    } catch (error) {
      console.error('Remove member error:', error)
      throw error
    }
  }

  // Search users for invitation
  async searchUsers(query, excludeGroupId = null) {
    try {
      const params = { q: query }
      if (excludeGroupId) {
        params.exclude_group = excludeGroupId
      }
      return await ApiService.get('/users/search', params)
    } catch (error) {
      console.error('Search users error:', error)
      throw error
    }
  }

  // Get admin users (EDCTP Admin and Super Admin roles)
  async getAdminUsers() {
    try {
      return await ApiService.get('/admins')
    } catch (error) {
      console.error('Get admin users error:', error)
      throw error
    }
  }

  // Add admin for working group
  async addWorkingGroupAdmin(workingGroupId, data) {
    try {
      return await ApiService.post(`/working-groups/${workingGroupId}/admins`, {}, data)
    } catch (error) {
      console.error('Add working group admin error:', error)
      throw error
    }
  }

  // Remove one admin from working group (or all when userId is omitted)
  async removeWorkingGroupAdmin(workingGroupId, userId = null) {
    try {
      const endpoint = userId
        ? `/working-groups/${workingGroupId}/admins/${userId}`
        : `/working-groups/${workingGroupId}/admins`

      return await ApiService.delete(endpoint)
    } catch (error) {
      console.error('Remove working group admin error:', error)
      throw error
    }
  }

  // Get admins of a working group
  async getWorkingGroupAdmins(workingGroupId) {
    try {
      return await ApiService.get(`/working-groups/${workingGroupId}/admins`)
    } catch (error) {
      console.error('Get working group admins error:', error)
      throw error
    }
  }

  /**
   * Request to join a working group
   */
  async requestToJoin(workingGroupId) {
    try {
      return await ApiService.post(`/working-groups/${workingGroupId}/request-to-join`)
    } catch (error) {
      console.error('Request to join working group error:', error)
      throw error
    }
  }

  /**
   * Request to leave a working group
   */
  async requestToLeave(workingGroupId) {
    try {
      return await ApiService.post(`/working-groups/${workingGroupId}/request-to-leave`)
    } catch (error) {
      console.error('Request to leave working group error:', error)
      throw error
    }
  }

  /**
   * Get pending approval requests for a working group
   */
  async getPendingApprovals(workingGroupId) {
    try {
      return await ApiService.get(`/working-groups/${workingGroupId}/pending-approvals`)
    } catch (error) {
      console.error('Get pending approvals error:', error)
      throw error
    }
  }

  /**
   * Approve a user's request to join working group
   */
  async approveRequest(workingGroupId, approvalId) {
    try {
      return await ApiService.post(`/working-groups/${workingGroupId}/approvals/${approvalId}/approve`)
    } catch (error) {
      console.error('Approve request error:', error)
      throw error
    }
  }

  /**
   * Reject a user's request to join working group
   */
  async rejectRequest(workingGroupId, approvalId) {
    try {
      return await ApiService.post(`/working-groups/${workingGroupId}/approvals/${approvalId}/reject`)
    } catch (error) {
      console.error('Reject request error:', error)
      throw error
    }
  }

  async acceptInvitation(groupId) {
    return await ApiService.post(`/working-groups/${groupId}/accept-invitation`)
  }

  async declineInvitation(groupId) {
    return await ApiService.post(`/working-groups/${groupId}/decline-invitation`)
  }

  /**
   * Get my approval status for working groups
   */
  async getMyApprovalStatus() {
    try {
      return await ApiService.get('/working-groups/my-approval-status')
    } catch (error) {
      console.error('Get my approval status error:', error)
      throw error
    }
  }

  /**
   * Get all working groups
   * @returns {Promise}
   */
  async getAll() {
    try {
      return await ApiService.get('/working-groups')
    } catch (error) {
      console.error('Get all working groups error:', error)
      throw error
    }
  }

  /**
   * Get working groups where the current user has been approved
   * This is used to check if a user can access forums linked to specific working groups
   * @returns {Promise}
   */
  async getUserApprovedGroups() {
    try {
      return await ApiService.get('/working-groups/user/approved')
    } catch (error) {
      console.error('Get user approved groups error:', error)
      throw error
    }
  }

  /**
   * Alias for compatibility
   */
  async getMyApprovals() {
    return this.getUserApprovedGroups()
  }
}

export default new WorkingGroupServices()
